/**
 * Sirena allarme stile sveglia via WebAudio API.
 * - Due frequenze alternate (typical "ee-oo" emergency tone)
 * - Volume alto
 * - Loop per N secondi
 * - Stoppabile in qualsiasi momento
 *
 * Usage:
 *   const alarm = createAlarm();
 *   alarm.play(7); // suona 7 secondi
 *   alarm.stop();  // ferma subito
 *
 * Note: WebAudio richiede un'interazione utente prima di poter suonare.
 * Chiamare ensureUnlocked() dentro un onClick handler (es. bottone "Avvia timer")
 * per "sbloccare" il contesto audio per usi successivi senza interazione diretta.
 */

let _ctx = null;
let _unlocked = false;

function getCtx() {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  return _ctx;
}

/**
 * Da chiamare dentro un user-gesture (click/tap) per autorizzare l'audio.
 * Necessario su iOS Safari e per autoplay policy.
 */
export async function ensureAlarmUnlocked() {
  const ctx = getCtx();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    if (!_unlocked) {
      // Suona un blip impercettibile per "primare" il contesto
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      _unlocked = true;
    }
    return true;
  } catch {
    return false;
  }
}

export function createAlarm() {
  let activeNodes = [];
  let stopTimer = null;
  let beepInterval = null;
  let toneToggle = false;

  const stop = () => {
    if (stopTimer) { clearTimeout(stopTimer); stopTimer = null; }
    if (beepInterval) { clearInterval(beepInterval); beepInterval = null; }
    activeNodes.forEach((n) => {
      try { n.stop?.(); } catch (_) { /* ignore */ }
      try { n.disconnect?.(); } catch (_) { /* ignore */ }
    });
    activeNodes = [];
    if (window.navigator?.vibrate) {
      try { window.navigator.vibrate(0); } catch (_) { /* ignore */ }
    }
  };

  const playOneBeep = (freq, durationMs) => {
    const ctx = getCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = freq;
    // Volume alto ma con attack/release per evitare click
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.6, now + 0.01);
    gain.gain.setValueAtTime(0.6, now + (durationMs / 1000) - 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + (durationMs / 1000));
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + (durationMs / 1000) + 0.05);
    activeNodes.push(osc, gain);
    // pulizia nodi quando scadono
    setTimeout(() => {
      activeNodes = activeNodes.filter((n) => n !== osc && n !== gain);
    }, durationMs + 100);
  };

  const play = async (durationSec = 7) => {
    stop(); // se già in corso, riparti pulito
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* ignore */ }
    }
    // Vibrazione continua (Android Chrome supporta fino a 10s)
    if (window.navigator?.vibrate) {
      // Pattern: vibra 400, pausa 100, ripetuto per durationSec
      const pattern = [];
      let total = 0;
      while (total < durationSec * 1000) {
        pattern.push(400, 100);
        total += 500;
      }
      try { window.navigator.vibrate(pattern); } catch (_) { /* ignore */ }
    }

    // Sirena: alterna 880Hz e 660Hz ogni 250ms (tono "uee-oo" emergency)
    const beepEvery = 250;
    toneToggle = false;
    playOneBeep(toneToggle ? 660 : 880, beepEvery - 20);
    toneToggle = !toneToggle;
    beepInterval = setInterval(() => {
      playOneBeep(toneToggle ? 660 : 880, beepEvery - 20);
      toneToggle = !toneToggle;
    }, beepEvery);

    stopTimer = setTimeout(() => stop(), durationSec * 1000);
  };

  return { play, stop };
}
